import React, { createContext, useContext, useEffect, useState } from 'react';
import i18n from '../i18n';

export interface LocaleContextType {
    lingua: string;
    setLingua: (lang: string) => void;
    locale: string;
    setLocale: (loc: string) => void;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const SUPPORTED_LANGUAGES = ['it', 'en', 'de', 'fr'] as const;

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const supported = SUPPORTED_LANGUAGES;
    const detectBrowser = () => {
        if (typeof navigator === 'undefined') return 'it';
        const navLang = (navigator.language || 'it').split('-')[0].toLowerCase();
        return supported.includes(navLang as typeof SUPPORTED_LANGUAGES[number]) ? navLang : 'it';
    };

    const stored = (typeof window !== 'undefined') ? localStorage.getItem('app_lang') : null;
    const initial = stored && supported.includes(stored as typeof SUPPORTED_LANGUAGES[number]) ? stored : detectBrowser();

    const [lingua, _setLingua] = useState(initial);
    const [locale, setLocale] = useState(initial);

    // Sync i18n on mount & when lingua changes
    useEffect(() => {
        try { i18n.changeLanguage(lingua); } catch { /* ignore */ }
    }, [lingua]);

    const setLingua = (lang: string) => {
        const norm = supported.includes(lang as typeof SUPPORTED_LANGUAGES[number]) ? lang : 'it';
        _setLingua(norm);
        setLocale(norm);
        try { localStorage.setItem('app_lang', norm); } catch { /* ignore */ }
    };

    return (
        <LocaleContext.Provider value={{ lingua, setLingua, locale, setLocale }}>
            {children}
        </LocaleContext.Provider>
    );
};

export const useLocale = () => {
    const context = useContext(LocaleContext);
    if (!context) throw new Error('useLocale must be used within a LocaleProvider');
    return context;
};